import _ from 'lodash'
import { Buffer } from 'buffer'
import React, { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useRequestHandler } from '@/hooks/useRequestHandler'

import ProfileForm, { ProfileFormHandle, ProfileFormValues } from '../components/ProfileForm'

import { UpdateAuthUserDto } from '@/core/classes/Auth'
import { StaffsPrivacy } from '@/core/models/staff.model'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser, selectAuthUserUpdateRequestState, updateMyProfile } from '@/redux/reducers/auth.reducer'
import { isStaff } from '@/utils/type-guards'

export default function ProfileFormContainer() {
  const profileFormRef = useRef<ProfileFormHandle>(null)
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { dispatch, requestTracker: authUserUpdateRequestTracker } = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.authUser.update.success'), { variant: 'success' })
      profileFormRef.current?.toggleEditable()
    },
    onError: () => enqueueSnackbar(t('notification.authUser.update.error'), { variant: 'error' }),
    requestTrackerSelector: selectAuthUserUpdateRequestState,
  })

  const authUser = useAppSelector(selectAuthUser)

  const profileFormInitialValues = useMemo(
    () => ({
      birthday: authUser?.userInfo.birthday ? new Date(authUser.userInfo.birthday) : null,
      city: authUser?.contactInfo?.city || '',
      cp: authUser?.contactInfo?.cp ? authUser.contactInfo?.cp.toString() : '',
      cps: authUser && isStaff(authUser) ? authUser.cps ?? '' : '',
      email: authUser?.email || '',
      gender: authUser?.userInfo.gender || ('' as ProfileFormValues['gender']),
      name: authUser?.userInfo.name || '',
      phone: authUser?.contactInfo?.phone || '',
      speciality: authUser && isStaff(authUser) ? authUser.speciality ?? '' : '',
      street: authUser?.contactInfo?.street || '',
      surname: authUser?.userInfo.surname || '',
      signature: authUser && isStaff(authUser) && authUser.signature ? Buffer.from(authUser.signature.data).toString() : '',
      baudrate: authUser && isStaff(authUser) ? authUser.baudrate ?? 9600 : 9600,
      privacy: authUser && isStaff(authUser) ? authUser.privacy ?? StaffsPrivacy.Establishment : StaffsPrivacy.Establishment,
      civility: authUser && isStaff(authUser) ? authUser.civility || '' : '',
      title: authUser && isStaff(authUser) ? authUser.title || '' : '',
      conflictOfInterest: authUser && isStaff(authUser) ? authUser.conflictOfInterest || '' : '',
    }),
    [authUser]
  )

  const transformData = (profileFormValues: ProfileFormValues): UpdateAuthUserDto => {
    let data = {
      contactInfo: {
        street: profileFormValues.street || null,
        city: profileFormValues.city || null,
        cp: profileFormValues.cp || null,
        phone: profileFormValues.phone || null,
      },
      userInfo: {
        birthday: profileFormValues.birthday !== null ? profileFormValues.birthday.toISOString() : null,
        gender: profileFormValues.gender,
        name: profileFormValues.name,
        surname: profileFormValues.surname,
      },
      cps: profileFormValues.cps === '' ? null : profileFormValues.cps,
      email: profileFormValues.email,
      speciality: profileFormValues.speciality || null,
      signature: {
        data: profileFormValues.signature ? _.replace(profileFormValues.signature, 'data:image/png;base64,', '') : '',
        contentType: 'image/png',
      },
      baudrate: profileFormValues.baudrate || 9600,
      privacy: profileFormValues.privacy || null,
      civility: (profileFormValues.civility as any) || null,
      title: profileFormValues.title || null,
      conflictOfInterest: profileFormValues.conflictOfInterest || null,
    }
    return data
  }

  const handleModifyProfile = (values: ProfileFormValues) => {
    dispatch(updateMyProfile(transformData(values)))
  }

  return (
    <ProfileForm
      ref={profileFormRef}
      initialValues={profileFormInitialValues}
      isSubmitting={authUserUpdateRequestTracker.loading}
      onSubmit={handleModifyProfile}
    />
  )
}
